import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import PeoplePlacesPage from "@/pages/PeoplePlacesPage";
import DashboardPage from "@/pages/DashboardPage";
import CompetitionsPage from "@/pages/CompetitionsPage";
import CompetitionDetailPage from "@/pages/CompetitionDetailPage";
import CompetitionPagesListPage from "@/pages/CompetitionPagesListPage";
import CompetitionPageEditorPage from "@/pages/CompetitionPageEditorPage";
import CompetitionPagePreview from "@/pages/CompetitionPagePreview";
import DocumentsPage from "@/pages/DocumentsPage";
import ContentLibraryPage from "@/pages/ContentLibraryPage";
import AnnualReviewPage from "@/pages/AnnualReviewPage";
import SystemSettingsPage from "@/pages/SystemSettingsPage";
import EventTimingPage from "@/pages/EventTimingPage";
import SkatersEntriesPage from "@/pages/SkatersEntriesPage";
import SkaterDeduplicationPage from "@/pages/SkaterDeduplicationPage";
import UsersAdminPage from "@/pages/UsersAdminPage";
import EmailDeliveryPage from "@/pages/EmailDeliveryPage";
import ClubsPage from "@/pages/ClubsPage";
import VenuesPage from "@/pages/VenuesPage";
import ApprovalsPage from "@/pages/ApprovalsPage";
import ComingCompetitionsPage from "@/pages/ComingCompetitionsPage";
import PublicCompetitionPage from "@/pages/PublicCompetitionPage";
import MyProfilePage from "@/pages/MyProfilePage";
import OrderOfSkatePage from "@/pages/OrderOfSkatePage";
import AuthCallback from "@/components/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import DashboardLayout from "@/components/DashboardLayout";
import PreviewBanner from "@/components/PreviewBanner";

// API base URL.
// In a browser, ALWAYS use the page's current origin so API calls are
// first-party with the page (essential for cookie-based auth — a cookie
// set by https://skate-doc-builder.emergent.host can't be sent by the
// browser when the user is on https://compcreator.nz, because modern
// browsers treat cross-site cookies as third-party and block them by
// default, especially in privacy/incognito modes).
// REACT_APP_BACKEND_URL is kept only as a non-browser fallback (tests,
// SSR-like contexts).
const BACKEND_URL =
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/coming-competitions" element={<ComingCompetitionsPage />} />
      <Route path="/competition/:pageId" element={<PublicCompetitionPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardLayout>
            <DashboardPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/profile" element={
        <ProtectedRoute>
          <DashboardLayout>
            <MyProfilePage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/competitions" element={
        <ProtectedRoute>
          <DashboardLayout>
            <CompetitionsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/competitions/:id" element={
        <ProtectedRoute>
          <DashboardLayout>
            <CompetitionDetailPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/competitions/:competitionId/order-of-skate" element={
        <ProtectedRoute>
          <DashboardLayout>
            <OrderOfSkatePage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/competition-pages" element={
        <ProtectedRoute>
          <DashboardLayout>
            <CompetitionPagesListPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/competition-pages/create" element={
        <ProtectedRoute>
          <DashboardLayout>
            <CompetitionPageEditorPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/competition-pages/:id" element={
        <ProtectedRoute>
          <DashboardLayout>
            <CompetitionPageEditorPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/documents" element={
        <ProtectedRoute>
          <DashboardLayout>
            <DocumentsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/content" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ContentLibraryPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/content/schedules/:scheduleId/annual-review" element={
        <ProtectedRoute>
          <DashboardLayout>
            <AnnualReviewPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/settings" element={
        <ProtectedRoute requireSuper>
          <DashboardLayout>
            <SystemSettingsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/event-timing" element={
        <ProtectedRoute>
          <DashboardLayout>
            <EventTimingPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/people-places" element={
        <ProtectedRoute>
          <DashboardLayout>
            <PeoplePlacesPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/users" element={
        <ProtectedRoute>
          <DashboardLayout>
            <UsersAdminPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/clubs" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ClubsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/venues" element={
        <ProtectedRoute>
          <DashboardLayout>
            <VenuesPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/skaters-entries" element={
        <ProtectedRoute>
          <DashboardLayout>
            <SkatersEntriesPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/skaters-entries/duplicates" element={
        <ProtectedRoute>
          <DashboardLayout>
            <SkaterDeduplicationPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/approvals" element={
        <ProtectedRoute requireSuper>
          <DashboardLayout>
            <ApprovalsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/email-delivery" element={
        <ProtectedRoute requireSuper>
          <DashboardLayout>
            <EmailDeliveryPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/preview/:pageId" element={
        <ProtectedRoute requireSuper>
          <CompetitionPagePreview />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <PreviewBanner />
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
